import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { AppDispatch, RootState } from "src/store/store";
import { setGlobalInfo } from "src/store/features/global";
import { GlobalUtils } from "src/utils/globalUtils";
import { ProfileService } from "src/rest/profileService";
import { AuthService } from "src/rest/authService";
import { UserAvatarIcon } from "src/resources/icons";
import LogoutIcon from "src/resources/icons/LogoutIcon";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { AppRoutes } from "src/common/constants/constantEnums";
import type { MarriageProfileDto } from "src/types/Profile";

interface Props {
  size?: number;
}

const ConsultantProfileSwitcher: React.FC<Props> = ({ size = 55 }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const activeId = useSelector((s: RootState) => s.globalInfo.activeMarriageProfileId);

  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState<MarriageProfileDto[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  // Track whether we've done at least one successful load so we can still
  // show cached data while re-fetching on subsequent opens.
  const [hasFetched, setHasFetched] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: containerRef as React.RefObject<HTMLElement>, handler: () => setOpen(false) });

  // Keep a ref to current object URLs so the cleanup can revoke them on unmount
  // without needing to list photoUrls as a dependency.
  const photoUrlsRef = useRef<Record<number, string>>({});

  const loadProfiles = useCallback(async () => {
    let cancelled = false;
    try {
      setLoading(true);
      const emailId = GlobalUtils.getUserLoginInfo()?.emailId || "";
      if (!emailId) return;
      const profile = await ProfileService.getProfile(emailId);
      const list: MarriageProfileDto[] = profile?.marriageProfiles || [];
      if (!cancelled) {
        setProfiles(list);
        setHasFetched(true);
      }

      // Auto-select first profile if none is active yet
      if (!activeId && list.length > 0 && !cancelled) {
        dispatch(setGlobalInfo({ activeMarriageProfileId: list[0].id }));
      }

      // Revoke old object URLs before fetching new ones
      Object.values(photoUrlsRef.current).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* ignore */ }
      });
      photoUrlsRef.current = {};
      setPhotoUrls({});

      const entries = await Promise.all(
        list.map(async (p) => {
          if (!p.profilePhoto) return null;
          try {
            const url = await fetchAuthedImageObjectUrl(p.profilePhoto);
            return [p.id, url] as const;
          } catch {
            return null;
          }
        })
      );

      if (!cancelled) {
        const urlMap: Record<number, string> = {};
        entries.forEach((e) => { if (e) urlMap[e[0]] = e[1]; });
        photoUrlsRef.current = urlMap;
        setPhotoUrls(urlMap);
      }
    } catch {
      // ignore
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [activeId, dispatch]);

  // Reload profiles every time the dropdown is opened
  useEffect(() => {
    if (!open) return;
    void loadProfiles();
  }, [open, loadProfiles]);

  // Revoke all object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(photoUrlsRef.current).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* ignore */ }
      });
    };
  }, []);

  const handleSelect = (id: number) => {
    dispatch(setGlobalInfo({ activeMarriageProfileId: id }));
    setOpen(false);
  };

  const handleManageProfiles = () => {
    setOpen(false);
    navigate(AppRoutes.PROFILE);
  };

  const handleLogout = async () => {
    setOpen(false);
    try { await AuthService.logout(); } catch { /* ignore */ }
    try {
      localStorage.removeItem("userRole");
      localStorage.removeItem("authToken");
      localStorage.removeItem("consultantInfo");
      localStorage.removeItem("activeMarriageProfileId");
      const bc = new BroadcastChannel("auth");
      bc.postMessage("logout");
      bc.close();
      sessionStorage.clear();
    } finally {
      navigate("/login", { replace: true, state: { fromApp: true } });
    }
  };

  return (
    <Box ref={containerRef} position="relative" display="inline-block">
      {/* Avatar trigger */}
      <Box
        cursor="pointer"
        onClick={() => setOpen((v) => !v)}
        borderRadius="full"
        transition="box-shadow 0.15s"
        _hover={{ boxShadow: "0 0 0 3px rgba(74,44,42,0.18)" }}
      >
        <UserAvatarIcon width={size} height={size} />
      </Box>

      {/* Dropdown */}
      {open && (
        <Box
          position="absolute"
          top={`calc(${size}px + 10px)`}
          right={0}
          zIndex={1000}
          bg="white"
          borderRadius="16px"
          boxShadow="0px 8px 32px rgba(0,0,0,0.14)"
          minW="280px"
          maxW="320px"
          overflow="hidden"
          border="1px solid rgba(108,117,125,0.12)"
        >
          {/* Header */}
          <Box px={5} pt={4} pb={3}>
            <HStack justify="center" spacing={2}>
              <Text fontWeight="700" fontSize="16px" color="#1F0812">
                Switch Profile
              </Text>
              {loading && hasFetched && <Spinner size="xs" color="#4A2C2A" />}
            </HStack>
          </Box>

          <Divider borderColor="rgba(108,117,125,0.15)" />

          {/* Profile list */}
          <VStack spacing={0} align="stretch" maxH="280px" overflowY="auto" py={2}>
            {loading && !hasFetched ? (
              <Flex justify="center" py={6}>
                <Spinner size="md" color="#4A2C2A" />
              </Flex>
            ) : profiles.length === 0 ? (
              <Text px={5} py={4} color="#6C757D" fontSize="sm" textAlign="center">
                No marriage profiles yet.
              </Text>
            ) : (
              profiles.map((p) => {
                const isActive = p.id === activeId;
                return (
                  <HStack
                    key={p.id}
                    px={4}
                    py={3}
                    spacing={3}
                    cursor="pointer"
                    bg={isActive ? "rgba(252,237,221,0.55)" : "white"}
                    borderRadius="10px"
                    mx={2}
                    transition="background 0.15s"
                    _hover={{ bg: "rgba(252,237,221,0.8)" }}
                    onClick={() => handleSelect(p.id)}
                  >
                    <Avatar
                      name={p.fullName || "Profile"}
                      src={photoUrls[p.id]}
                      size="sm"
                      bg="#FCEDDD"
                      color="#4A2C2A"
                    />
                    <Flex direction="column" flex={1} minW={0}>
                      <Text fontWeight="600" fontSize="14px" color="#1F0812" noOfLines={1}>
                        {p.fullName || "Marriage Profile"}
                      </Text>
                      <Text fontSize="12px" color="#6C757D" noOfLines={1} textTransform="uppercase" letterSpacing="0.04em">
                        {p.maritalStatus || ""}
                      </Text>
                    </Flex>
                    {isActive && (
                      <Box
                        bg="#4A2C2A"
                        borderRadius="full"
                        w="22px"
                        h="22px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <CheckIcon color="white" boxSize="10px" />
                      </Box>
                    )}
                  </HStack>
                );
              })
            )}
          </VStack>

          <Divider borderColor="rgba(108,117,125,0.15)" />

          {/* Actions */}
          <VStack spacing={0} align="stretch" py={2}>
            <Text
              px={5}
              py={3}
              fontSize="14px"
              fontWeight="600"
              color="#1F0812"
              cursor="pointer"
              textAlign="center"
              _hover={{ bg: "rgba(108,117,125,0.06)" }}
              onClick={handleManageProfiles}
            >
              Manage Profiles
            </Text>
            <Text
              px={5}
              py={3}
              fontSize="14px"
              fontWeight="500"
              color="#6C757D"
              cursor="pointer"
              textAlign="center"
              _hover={{ bg: "rgba(108,117,125,0.06)" }}
              onClick={handleLogout}
            >
              <HStack justify="center" spacing={2}>
                <LogoutIcon />
                <span>Sign Out</span>
              </HStack>
            </Text>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default ConsultantProfileSwitcher;
